import { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth, API } from "../App";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import ProfilePictureUpload from "../components/ProfilePictureUpload";
import Sidebar from "../components/Sidebar";
import { countries, statesByCountry, nationalities, industries, months, generateYears, generateDays } from "../data/formOptions";
import {
  Zap,
  ArrowLeft,
  Upload,
  Linkedin,
  FileText,
  Sparkles,
  Plus,
  X,
  Save,
  Link as LinkIcon,
  Home,
  Building2,
  Calendar,
  Briefcase,
  Check,
  AlertCircle,
  AlertTriangle,
  ChevronDown,
  ChevronUp,
  Trash2,
  GraduationCap,
  Award,
  Heart,
  ExternalLink,
  Globe
} from "lucide-react";

const ProfileBuilder = () => {
  const navigate = useNavigate();
  const { user, setUser } = useAuth();
  const fileInputRef = useRef(null);
  const [profile, setProfile] = useState({
    full_name: "",
    date_of_birth: "",
    phone: "",
    gender: "",
    nationality: "",
    address: "",
    headline: "",
    summary: "",
    skills: [],
    experience: [],
    education: [],
    certifications: [],
    memberships: [],
    volunteer_work: [],
    location: "",
    remote_preference: "flexible",
    career_level: "mid",
    industries: [],
    employment_types: []
  });
  const [linkedinContent, setLinkedinContent] = useState("");
  const [linkedinUrl, setLinkedinUrl] = useState("");
  const [newSkill, setNewSkill] = useState("");
  const [newIndustry, setNewIndustry] = useState("");
  const [loading, setLoading] = useState(false);
  const [parsing, setParsing] = useState(false);
  const [parsingUrl, setParsingUrl] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  const [importMethod, setImportMethod] = useState("content"); // "content", "url", "pdf"
  const [showAddExperience, setShowAddExperience] = useState(false);
  const [newExperience, setNewExperience] = useState({
    title: "",
    company: "",
    location: "",
    start_date: "",
    end_date: "",
    is_current: false,
    description: ""
  });
  const [editingExpIndex, setEditingExpIndex] = useState(null);
  
  // Education states
  const [showAddEducation, setShowAddEducation] = useState(false);
  const [newEducation, setNewEducation] = useState({
    degree: "",
    field_of_study: "",
    school: "",
    start_year: "",
    end_year: "",
    is_current: false,
    description: ""
  });
  const [editingEduIndex, setEditingEduIndex] = useState(null);
  
  // Certification states
  const [showAddCertification, setShowAddCertification] = useState(false);
  const [newCertification, setNewCertification] = useState({
    name: "",
    issuing_organization: "",
    issue_date: "",
    expiry_date: "",
    credential_id: "",
    credential_url: ""
  });
  const [editingCertIndex, setEditingCertIndex] = useState(null);
  
  // Volunteer Work states
  const [showAddVolunteer, setShowAddVolunteer] = useState(false);
  const [newVolunteer, setNewVolunteer] = useState({
    role: "",
    organization: "",
    cause: "",
    start_date: "",
    end_date: "",
    is_current: false,
    description: ""
  });
  const [editingVolunteerIndex, setEditingVolunteerIndex] = useState(null);

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await fetch(`${API}/profile/jobseeker`, {
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        if (data.headline || data.skills?.length) {
          setProfile(data);
        }
      }
    } catch (error) {
      console.error("Failed to fetch profile:", error);
    }
  };

  const handleParseLinkedIn = async () => {
    if (!linkedinContent.trim()) {
      toast.error("Please paste your LinkedIn content");
      return;
    }

    setParsing(true);
    try {
      const response = await fetch(`${API}/profile/parse-linkedin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ content: linkedinContent }),
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          headline: data.headline || prev.headline,
          summary: data.summary || prev.summary,
          skills: data.skills || prev.skills,
          experience: data.experience || prev.experience,
          education: data.education || prev.education,
          location: data.location || prev.location,
        }));
        toast.success("LinkedIn content parsed successfully!");
        setLinkedinContent("");
      } else {
        toast.error("Failed to parse LinkedIn content");
      }
    } catch (error) {
      toast.error("Failed to parse LinkedIn content");
    } finally {
      setParsing(false);
    }
  };

  const handleParseLinkedInUrl = async () => {
    if (!linkedinUrl.trim() || !linkedinUrl.includes("linkedin.com")) {
      toast.error("Please enter a valid LinkedIn URL");
      return;
    }

    setParsingUrl(true);
    try {
      const response = await fetch(`${API}/profile/parse-linkedin-url`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ url: linkedinUrl }),
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          headline: data.headline || prev.headline,
          summary: data.summary || prev.summary,
          skills: data.skills || prev.skills,
          experience: data.experience || prev.experience,
          education: data.education || prev.education,
          location: data.location || prev.location,
        }));
        toast.success("LinkedIn profile extracted successfully!");
        setLinkedinUrl("");
      } else {
        toast.error("Failed to extract LinkedIn profile");
      }
    } catch (error) {
      toast.error("Failed to extract LinkedIn profile");
    } finally {
      setParsingUrl(false);
    }
  };

  const handleUploadPdf = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf') && !file.name.toLowerCase().endsWith('.docx')) {
      toast.error("Please upload a PDF or Word (.docx) file");
      return;
    }

    setUploadingPdf(true);
    try {
      const formData = new FormData();
      formData.append("file", file);

      const response = await fetch(`${API}/profile/parse-resume`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      if (response.ok) {
        const data = await response.json();
        setProfile(prev => ({
          ...prev,
          headline: data.headline || prev.headline,
          summary: data.summary || prev.summary,
          skills: data.skills || prev.skills,
          experience: data.experience || prev.experience,
          education: data.education || prev.education,
          location: data.location || prev.location,
        }));
        toast.success("Resume parsed successfully!");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to parse resume");
      }
    } catch (error) {
      toast.error("Failed to parse resume");
    } finally {
      setUploadingPdf(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleSaveProfile = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/profile/jobseeker`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(profile),
      });

      if (response.ok) {
        toast.success("Profile saved successfully!");
      } else {
        toast.error("Failed to save profile");
      }
    } catch (error) {
      toast.error("Failed to save profile");
    } finally {
      setLoading(false);
    }
  };

  const addSkill = () => {
    if (newSkill.trim() && !profile.skills.includes(newSkill.trim())) {
      setProfile(prev => ({ ...prev, skills: [...prev.skills, newSkill.trim()] }));
      setNewSkill("");
    }
  };

  const removeSkill = (skill) => {
    setProfile(prev => ({ ...prev, skills: prev.skills.filter(s => s !== skill) }));
  };

  const addIndustry = () => {
    if (newIndustry.trim() && !profile.industries.includes(newIndustry.trim())) {
      setProfile(prev => ({ ...prev, industries: [...prev.industries, newIndustry.trim()] }));
      setNewIndustry("");
    }
  };

  const removeIndustry = (industry) => {
    setProfile(prev => ({ ...prev, industries: prev.industries.filter(i => i !== industry) }));
  };

  const addExperience = () => {
    if (!newExperience.title || !newExperience.company) {
      toast.error("Please fill in job title and company");
      return;
    }
    
    const exp = {
      ...newExperience,
      end_date: newExperience.is_current ? "Present" : newExperience.end_date
    };
    
    if (editingExpIndex !== null) {
      // Update existing
      setProfile(prev => ({
        ...prev,
        experience: prev.experience.map((e, i) => i === editingExpIndex ? exp : e)
      }));
      setEditingExpIndex(null);
    } else {
      // Add new
      setProfile(prev => ({
        ...prev,
        experience: [...prev.experience, exp]
      }));
    }
    
    setNewExperience({
      title: "",
      company: "",
      location: "",
      start_date: "",
      end_date: "",
      is_current: false,
      description: ""
    });
    setShowAddExperience(false);
    toast.success(editingExpIndex !== null ? "Experience updated!" : "Experience added!");
  };

  const editExperience = (index) => {
    const exp = profile.experience[index];
    setNewExperience({
      title: exp.title || "",
      company: exp.company || "",
      location: exp.location || "",
      start_date: exp.start_date || exp.duration?.split(" - ")[0] || "",
      end_date: exp.end_date === "Present" ? "" : (exp.end_date || exp.duration?.split(" - ")[1] || ""),
      is_current: exp.end_date === "Present" || exp.duration?.includes("Present"),
      description: exp.description || ""
    });
    setEditingExpIndex(index);
    setShowAddExperience(true);
  };

  const removeExperience = (index) => {
    setProfile(prev => ({
      ...prev,
      experience: prev.experience.filter((_, i) => i !== index)
    }));
    toast.success("Experience removed");
  };

  // Education functions
  const addEducation = () => {
    if (!newEducation.degree || !newEducation.school) {
      toast.error("Please fill in degree and school");
      return;
    }
    
    const edu = {
      ...newEducation,
      end_year: newEducation.is_current ? "Present" : newEducation.end_year
    };
    
    if (editingEduIndex !== null) {
      setProfile(prev => ({
        ...prev,
        education: prev.education.map((e, i) => i === editingEduIndex ? edu : e)
      }));
      setEditingEduIndex(null);
    } else {
      setProfile(prev => ({
        ...prev,
        education: [...(prev.education || []), edu]
      }));
    }
    
    setNewEducation({
      degree: "",
      field_of_study: "",
      school: "",
      start_year: "",
      end_year: "",
      is_current: false,
      description: ""
    });
    setShowAddEducation(false);
    toast.success(editingEduIndex !== null ? "Education updated!" : "Education added!");
  };

  const editEducation = (index) => {
    const edu = profile.education[index];
    setNewEducation({
      degree: edu.degree || "",
      field_of_study: edu.field_of_study || edu.field || "",
      school: edu.school || "",
      start_year: edu.start_year || "",
      end_year: edu.end_year === "Present" ? "" : (edu.end_year || edu.year || ""),
      is_current: edu.end_year === "Present",
      description: edu.description || ""
    });
    setEditingEduIndex(index);
    setShowAddEducation(true);
  };

  const removeEducation = (index) => {
    setProfile(prev => ({
      ...prev,
      education: prev.education.filter((_, i) => i !== index)
    }));
    toast.success("Education removed");
  };

  // Certification functions
  const addCertification = () => {
    if (!newCertification.name || !newCertification.issuing_organization) {
      toast.error("Please fill in certification name and issuing organization");
      return;
    }
    
    if (editingCertIndex !== null) {
      setProfile(prev => ({
        ...prev,
        certifications: prev.certifications.map((c, i) => i === editingCertIndex ? newCertification : c)
      }));
      setEditingCertIndex(null);
    } else {
      setProfile(prev => ({
        ...prev,
        certifications: [...(prev.certifications || []), newCertification]
      }));
    }
    
    setNewCertification({
      name: "",
      issuing_organization: "",
      issue_date: "",
      expiry_date: "",
      credential_id: "",
      credential_url: ""
    });
    setShowAddCertification(false);
    toast.success(editingCertIndex !== null ? "Certification updated!" : "Certification added!");
  };

  const editCertification = (index) => {
    const cert = profile.certifications[index];
    setNewCertification({
      name: cert.name || "",
      issuing_organization: cert.issuing_organization || "",
      issue_date: cert.issue_date || "",
      expiry_date: cert.expiry_date || "",
      credential_id: cert.credential_id || "",
      credential_url: cert.credential_url || ""
    });
    setEditingCertIndex(index);
    setShowAddCertification(true);
  };

  const removeCertification = (index) => {
    setProfile(prev => ({
      ...prev,
      certifications: prev.certifications.filter((_, i) => i !== index)
    }));
    toast.success("Certification removed");
  };

  // Volunteer Work functions
  const addVolunteer = () => {
    if (!newVolunteer.role || !newVolunteer.organization) {
      toast.error("Please fill in role and organization");
      return;
    }
    
    const vol = {
      ...newVolunteer,
      end_date: newVolunteer.is_current ? "Present" : newVolunteer.end_date
    };
    
    if (editingVolunteerIndex !== null) {
      setProfile(prev => ({
        ...prev,
        volunteer_work: prev.volunteer_work.map((v, i) => i === editingVolunteerIndex ? vol : v)
      }));
      setEditingVolunteerIndex(null);
    } else {
      setProfile(prev => ({
        ...prev,
        volunteer_work: [...(prev.volunteer_work || []), vol]
      }));
    }
    
    setNewVolunteer({
      role: "",
      organization: "",
      cause: "",
      start_date: "",
      end_date: "",
      is_current: false,
      description: ""
    });
    setShowAddVolunteer(false);
    toast.success(editingVolunteerIndex !== null ? "Volunteer work updated!" : "Volunteer work added!");
  };

  const editVolunteer = (index) => {
    const vol = profile.volunteer_work[index];
    setNewVolunteer({
      role: vol.role || "",
      organization: vol.organization || "",
      cause: vol.cause || "",
      start_date: vol.start_date || "",
      end_date: vol.end_date === "Present" ? "" : (vol.end_date || ""),
      is_current: vol.end_date === "Present",
      description: vol.description || ""
    });
    setEditingVolunteerIndex(index);
    setShowAddVolunteer(true);
  };

  const removeVolunteer = (index) => {
    setProfile(prev => ({
      ...prev,
      volunteer_work: prev.volunteer_work.filter((_, i) => i !== index)
    }));
    toast.success("Volunteer work removed");
  };

  // Calculate profile completion
  const getProfileCompletion = () => {
    const fields = [
      { key: "full_name", label: "Full Name", completed: !!(profile.full_name || user?.name) },
      { key: "headline", label: "Professional Headline", completed: !!profile.headline },
      { key: "summary", label: "Professional Summary", completed: !!profile.summary },
      { key: "location", label: "Location", completed: !!profile.location },
      { key: "skills", label: "Skills (at least 3)", completed: profile.skills?.length >= 3 },
      { key: "experience", label: "Work History (at least 1)", completed: profile.experience?.length >= 1 },
      { key: "education", label: "Education (at least 1)", completed: profile.education?.length >= 1 },
      { key: "industries", label: "Target Industries", completed: profile.industries?.length >= 1 }
    ];
    
    const completed = fields.filter(f => f.completed).length;
    const percentage = Math.round((completed / fields.length) * 100);
    
    return { fields, completed, total: fields.length, percentage };
  };

  const completion = getProfileCompletion();

  return (
    <div className="min-h-screen bg-obsidian">
      <Sidebar />
      
      <main className="lg:ml-64 min-h-screen p-4 sm:p-6 pt-20 lg:pt-8">
        <div className="max-w-4xl mx-auto">
          {/* Page Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 sm:mb-8">
            <div>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold mb-1 sm:mb-2">
                MY <span className="text-crimson">PROFILE</span>
              </h1>
              <p className="text-gray-400 text-sm sm:text-base">Build your professional profile</p>
            </div>
            <Button
              onClick={handleSaveProfile}
              disabled={loading}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider text-sm w-full sm:w-auto"
              data-testid="save-profile-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save Profile
                </>
              )}
            </Button>
          </div>

          {/* Profile Completion Indicator */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-4 sm:p-6 mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-0 mb-4">
              <div className="flex items-center gap-3 sm:gap-4">
                <ProfilePictureUpload
                  currentImage={user?.picture}
                  uploadType="profile"
                  size="large"
                  onUploadSuccess={(url) => {
                    setUser(prev => ({ ...prev, picture: url }));
                  }}
                />
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-bold">PROFILE STRENGTH</h3>
                  <p className="text-gray-500 text-xs sm:text-sm">Click photo to upload</p>
                </div>
              </div>
              <span className={`text-xl sm:text-2xl font-bold ${
                completion.percentage >= 80 ? "text-green-400" : 
                completion.percentage >= 50 ? "text-yellow-400" : "text-orange-400"
              }`}>
                {completion.percentage}%
              </span>
            </div>
            
            <div className="h-2 bg-[#1a1a1a] mb-4 overflow-hidden">
              <motion.div 
                className={`h-full ${
                  completion.percentage >= 80 ? "bg-green-500" : 
                  completion.percentage >= 50 ? "bg-yellow-500" : "bg-orange-500"
                }`}
                initial={{ width: 0 }}
                animate={{ width: `${completion.percentage}%` }}
                transition={{ duration: 0.5 }}
              />
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {completion.fields.map((field) => (
                <div 
                  key={field.key}
                  className={`flex items-center gap-2 text-sm ${
                    field.completed ? "text-green-400" : "text-gray-500"
                  }`}
                >
                  {field.completed ? (
                    <Check className="w-4 h-4" />
                  ) : (
                    <AlertCircle className="w-4 h-4" />
                  )}
                  <span>{field.label}</span>
                </div>
              ))}
            </div>
            
            {completion.percentage < 100 && (
              <p className="text-gray-500 text-sm mt-4">
                Complete your profile to improve visibility and get better job matches!
              </p>
            )}
          </motion.div>
          {/* Import Options */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-4 sm:p-6 mb-6 sm:mb-8"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <h3 className="font-heading text-lg sm:text-xl font-bold mb-3 sm:mb-4">IMPORT YOUR PROFILE</h3>
            <p className="text-gray-400 text-xs sm:text-sm mb-4 sm:mb-6">Choose how you want to import your professional data</p>

            {/* Import Method Tabs */}
            <div className="flex flex-wrap gap-2 mb-4 sm:mb-6">
              <button
                onClick={() => setImportMethod("content")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm transition-colors ${
                  importMethod === "content" ? "bg-crimson text-white" : "bg-[#1a1a1a] text-gray-400 hover:text-white"
                }`}
                data-testid="import-tab-content"
              >
                <FileText className="w-3 h-3 sm:w-4 sm:h-4" />
                Paste<span className="hidden sm:inline"> Content</span>
              </button>
              <button
                onClick={() => setImportMethod("url")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm transition-colors ${
                  importMethod === "url" ? "bg-crimson text-white" : "bg-[#1a1a1a] text-gray-400 hover:text-white"
                }`}
                data-testid="import-tab-url"
              >
                <Globe className="w-3 h-3 sm:w-4 sm:h-4" />
                LinkedIn<span className="hidden sm:inline"> URL</span>
              </button>
              <button
                onClick={() => setImportMethod("pdf")}
                className={`flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 text-xs sm:text-sm transition-colors ${
                  importMethod === "pdf" ? "bg-crimson text-white" : "bg-[#1a1a1a] text-gray-400 hover:text-white"
                }`}
                data-testid="import-tab-pdf"
              >
                <Upload className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Upload </span>PDF
              </button>
            </div>

            {/* Content Paste */}
            {importMethod === "content" && (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-crimson" />
                  </div>
                  <div>
                    <h4 className="font-bold">Paste Profile Content</h4>
                    <p className="text-gray-500 text-sm">Copy and paste your resume or LinkedIn profile sections</p>
                  </div>
                </div>
                <div className="bg-blue-500/10 border border-blue-500/20 p-3 mb-4 text-xs text-blue-300 leading-relaxed" data-testid="linkedin-paste-guide">
                  <p className="font-semibold mb-1">How to copy from LinkedIn:</p>
                  <ol className="list-decimal list-inside space-y-0.5 text-blue-400">
                    <li>Open your LinkedIn profile in a browser</li>
                    <li>Select all text (Ctrl+A / Cmd+A) or highlight specific sections</li>
                    <li>Copy (Ctrl+C / Cmd+C) and paste below</li>
                    <li>Include: About, Experience, Education, and Skills sections</li>
                  </ol>
                </div>
                <Textarea
                  value={linkedinContent}
                  onChange={(e) => setLinkedinContent(e.target.value)}
                  placeholder="Paste your LinkedIn profile content here — About section, Experience, Skills, Education, etc."
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600 min-h-[150px] mb-4"
                  data-testid="linkedin-content-input"
                />
                <Button
                  onClick={handleParseLinkedIn}
                  disabled={parsing || !linkedinContent.trim()}
                  className="bg-crimson hover:bg-crimson/80 text-white uppercase tracking-wider"
                  data-testid="parse-linkedin-btn"
                >
                  {parsing ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Sparkles className="w-4 h-4 mr-2" />
                  )}
                  Parse Content
                </Button>
              </div>
            )}

            {/* LinkedIn URL Import */}
            {importMethod === "url" && (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                    <Globe className="w-5 h-5 text-crimson" />
                  </div>
                  <div>
                    <h4 className="font-bold">Import from LinkedIn URL</h4>
                    <p className="text-gray-500 text-sm">Enter your profile URL and we'll extract what we can</p>
                  </div>
                </div>
                <div className="bg-amber-500/10 border border-amber-500/20 p-3 mb-4 text-xs text-amber-300 leading-relaxed" data-testid="linkedin-url-warning">
                  <p className="font-semibold mb-1">Important:</p>
                  <p>LinkedIn limits what public profiles show. We'll extract your name and headline, then use AI to build a starting profile. <span className="text-white">You'll need to review and update the generated data.</span></p>
                  <p className="mt-1">For best results, use <button onClick={() => setImportMethod("content")} className="text-crimson underline hover:text-crimson/80">Paste Content</button> or <button onClick={() => setImportMethod("pdf")} className="text-crimson underline hover:text-crimson/80">Upload PDF</button> instead.</p>
                </div>
                <div className="flex gap-2 mb-4">
                  <input
                    type="url"
                    value={linkedinUrl}
                    onChange={(e) => setLinkedinUrl(e.target.value)}
                    placeholder="https://www.linkedin.com/in/your-profile"
                    className="flex-1 bg-obsidian border border-[#333] px-3 py-2.5 text-sm text-white placeholder:text-gray-600 focus:border-crimson focus:outline-none"
                    data-testid="linkedin-url-input"
                  />
                </div>
                <Button
                  onClick={handleParseLinkedInUrl}
                  disabled={parsingUrl || !linkedinUrl.trim()}
                  className="bg-crimson hover:bg-crimson/80 text-white uppercase tracking-wider"
                  data-testid="parse-linkedin-url-btn"
                >
                  {parsingUrl ? (
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Globe className="w-4 h-4 mr-2" />
                  )}
                  Import from LinkedIn
                </Button>
              </div>
            )}

            {/* PDF Upload */}
            {importMethod === "pdf" && (
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-crimson/20 flex items-center justify-center">
                    <FileText className="w-5 h-5 text-crimson" />
                  </div>
                  <div>
                    <h4 className="font-bold">Upload Resume PDF</h4>
                    <p className="text-gray-500 text-sm">Upload your resume (PDF or Word) and we&apos;ll extract your experience</p>
                  </div>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleUploadPdf}
                  accept=".pdf,.docx"
                  className="hidden"
                />
                <Button
                  onClick={() => fileInputRef.current?.click()}
                  disabled={uploadingPdf}
                  variant="outline"
                  className="border-crimson text-crimson hover:bg-crimson hover:text-white uppercase tracking-wider"
                  data-testid="upload-pdf-btn"
                >
                  {uploadingPdf ? (
                    <div className="w-5 h-5 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                  ) : (
                    <Upload className="w-4 h-4 mr-2" />
                  )}
                  {uploadingPdf ? "Parsing..." : "Choose PDF File"}
                </Button>
              </div>
            )}
          </motion.div>

          {/* Personal Information */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <h3 className="font-heading text-xl font-bold mb-6">PERSONAL INFORMATION</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                  Full Name *
                </Label>
                <Input
                  value={profile.full_name || user?.name || ""}
                  onChange={(e) => setProfile(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Your full name"
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                  data-testid="full-name-input"
                />
              </div>

              <div>
                <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                  Email (from account)
                </Label>
                <Input
                  value={user?.email || ""}
                  disabled
                  className="bg-obsidian border-[#333] text-gray-500 cursor-not-allowed"
                />
              </div>

              <div>
                <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                  Phone Number
                </Label>
                <Input
                  type="tel"
                  value={profile.phone || ""}
                  onChange={(e) => setProfile(prev => ({ ...prev, phone: e.target.value }))}
                  placeholder="+1 234 567 8900"
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                  data-testid="phone-input"
                />
              </div>

              <div>
                <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                  Date of Birth
                </Label>
                <div className="grid grid-cols-3 gap-2">
                  <select
                    value={profile.birth_month || ""}
                    onChange={(e) => setProfile(prev => ({ ...prev, birth_month: e.target.value }))}
                    className="w-full h-10 px-2 bg-obsidian border border-[#333] text-white text-sm focus:border-crimson focus:ring-1 focus:ring-crimson"
                    data-testid="birth-month-select"
                  >
                    <option value="">Month</option>
                    {months.map((month, idx) => (
                      <option key={month} value={String(idx + 1).padStart(2, '0')}>{month}</option>
                    ))}
                  </select>
                  <select
                    value={profile.birth_day || ""}
                    onChange={(e) => setProfile(prev => ({ ...prev, birth_day: e.target.value }))}
                    className="w-full h-10 px-2 bg-obsidian border border-[#333] text-white text-sm focus:border-crimson focus:ring-1 focus:ring-crimson"
                    data-testid="birth-day-select"
                  >
                    <option value="">Day</option>
                    {generateDays().map(day => (
                      <option key={day} value={String(day).padStart(2, '0')}>{day}</option>
                    ))}
                  </select>
                  <select
                    value={profile.birth_year || ""}
                    onChange={(e) => setProfile(prev => ({ ...prev, birth_year: e.target.value }))}
                    className="w-full h-10 px-2 bg-obsidian border border-[#333] text-white text-sm focus:border-crimson focus:ring-1 focus:ring-crimson"
                    data-testid="birth-year-select"
                  >
                    <option value="">Year</option>
                    {generateYears().map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                  Gender
                </Label>
                <select
                  value={profile.gender || ""}
                  onChange={(e) => setProfile(prev => ({ ...prev, gender: e.target.value }))}
                  className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                  data-testid="gender-select"
                >
                  <option value="">Prefer not to say</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="non-binary">Non-binary</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                  Nationality
                </Label>
                <select
                  value={profile.nationality || ""}
                  onChange={(e) => setProfile(prev => ({ ...prev, nationality: e.target.value }))}
                  className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                  data-testid="nationality-select"
                >
                  <option value="">Select nationality</option>
                  {nationalities.map(nat => (
                    <option key={nat} value={nat}>{nat}</option>
                  ))}
                </select>
              </div>

              {/* Location Section */}
              <div className="md:col-span-2 space-y-4">
                <Label className="text-gray-400 uppercase tracking-wider text-xs block">
                  Location
                </Label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <Label className="text-gray-500 text-xs mb-1 block">Country</Label>
                    <select
                      value={profile.country || ""}
                      onChange={(e) => setProfile(prev => ({ ...prev, country: e.target.value, state: "" }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                      data-testid="country-select"
                    >
                      <option value="">Select country</option>
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs mb-1 block">State/Region</Label>
                    <select
                      value={profile.state || ""}
                      onChange={(e) => setProfile(prev => ({ ...prev, state: e.target.value }))}
                      className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                      data-testid="state-select"
                      disabled={!profile.country || !statesByCountry[profile.country]}
                    >
                      <option value="">Select state/region</option>
                      {profile.country && statesByCountry[profile.country]?.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-gray-500 text-xs mb-1 block">City</Label>
                    <Input
                      value={profile.city || ""}
                      onChange={(e) => setProfile(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="Enter city"
                      className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                      data-testid="city-input"
                    />
                  </div>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-3">
                  <div className="flex items-center justify-between p-3 bg-obsidian border border-[#333]">
                    <Label className="text-gray-400 text-sm">Open to Remote Work</Label>
                    <button
                      type="button"
                      onClick={() => setProfile(prev => ({ ...prev, open_to_remote: !prev.open_to_remote }))}
                      className={`w-12 h-6 rounded-full transition-colors ${profile.open_to_remote ? 'bg-crimson' : 'bg-gray-600'}`}
                      data-testid="remote-toggle"
                    >
                      <span className={`block w-5 h-5 rounded-full bg-white transform transition-transform ${profile.open_to_remote ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                  <div className="flex items-center justify-between p-3 bg-obsidian border border-[#333]">
                    <Label className="text-gray-400 text-sm">Willing to Relocate</Label>
                    <button
                      type="button"
                      onClick={() => setProfile(prev => ({ ...prev, willing_to_relocate: !prev.willing_to_relocate }))}
                      className={`w-12 h-6 rounded-full transition-colors ${profile.willing_to_relocate ? 'bg-crimson' : 'bg-gray-600'}`}
                      data-testid="relocate-toggle"
                    >
                      <span className={`block w-5 h-5 rounded-full bg-white transform transition-transform ${profile.willing_to_relocate ? 'translate-x-6' : 'translate-x-0.5'}`} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Basic Info */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <h3 className="font-heading text-xl font-bold mb-6">BASIC INFORMATION</h3>
            
            <div className="space-y-4">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-gray-400 uppercase tracking-wider text-xs">
                    Professional Headline
                  </Label>
                  <Button
                    onClick={async () => {
                      if (!profile.skills.length && !profile.experience.length) {
                        toast.error("Please add skills or experience first");
                        return;
                      }
                      toast.info("Generating headline...");
                      try {
                        const response = await fetch(`${API}/profile/generate-headline`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            skills: profile.skills,
                            experience: profile.experience,
                            industries: profile.industries,
                            career_level: profile.career_level
                          })
                        });
                        if (response.ok) {
                          const data = await response.json();
                          setProfile(prev => ({ ...prev, headline: data.headline }));
                          toast.success("Headline generated!");
                        } else {
                          toast.error("Failed to generate headline");
                        }
                      } catch (err) {
                        toast.error("Failed to generate headline");
                      }
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-crimson hover:text-crimson/80 text-xs"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Auto-generate
                  </Button>
                </div>
                <Input
                  value={profile.headline}
                  onChange={(e) => setProfile(prev => ({ ...prev, headline: e.target.value }))}
                  placeholder="e.g., Senior Software Engineer | Full Stack Developer"
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                  data-testid="headline-input"
                />
              </div>

              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label className="text-gray-400 uppercase tracking-wider text-xs">
                    Professional Summary
                  </Label>
                  <Button
                    onClick={async () => {
                      if (!profile.headline && !profile.skills.length && !profile.experience.length) {
                        toast.error("Please add some profile information first");
                        return;
                      }
                      toast.info("Generating summary based on your profile...");
                      try {
                        const response = await fetch(`${API}/profile/generate-summary`, {
                          method: "POST",
                          headers: { "Content-Type": "application/json" },
                          credentials: "include",
                          body: JSON.stringify({
                            headline: profile.headline,
                            skills: profile.skills,
                            experience: profile.experience,
                            education: profile.education
                          })
                        });
                        if (response.ok) {
                          const data = await response.json();
                          setProfile(prev => ({ ...prev, summary: data.summary }));
                          toast.success("Summary generated!");
                        } else {
                          toast.error("Failed to generate summary");
                        }
                      } catch (err) {
                        toast.error("Failed to generate summary");
                      }
                    }}
                    variant="ghost"
                    size="sm"
                    className="text-crimson hover:text-crimson/80 text-xs"
                  >
                    <Sparkles className="w-3 h-3 mr-1" /> Auto-generate
                  </Button>
                </div>
                <Textarea
                  value={profile.summary}
                  onChange={(e) => setProfile(prev => ({ ...prev, summary: e.target.value }))}
                  placeholder="Brief description of your professional background and career goals..."
                  className="bg-obsidian border-[#333] text-white placeholder:text-gray-600 min-h-[180px] w-full resize-y"
                  data-testid="summary-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Career Level
                  </Label>
                  <select
                    value={profile.career_level}
                    onChange={(e) => setProfile(prev => ({ ...prev, career_level: e.target.value }))}
                    className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                    data-testid="career-level-select"
                  >
                    <option value="entry">Entry Level</option>
                    <option value="mid">Mid Level</option>
                    <option value="senior">Senior Level</option>
                    <option value="lead">Lead / Manager</option>
                    <option value="executive">Executive</option>
                  </select>
                </div>

                <div>
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Remote Preference
                  </Label>
                  <select
                    value={profile.remote_preference}
                    onChange={(e) => setProfile(prev => ({ ...prev, remote_preference: e.target.value }))}
                    className="w-full h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                    data-testid="remote-preference-select"
                  >
                    <option value="remote">Remote Only</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="onsite">On-site</option>
                    <option value="flexible">Flexible</option>
                  </select>
                </div>
              </div>
              
              {/* Employment Type Preference */}
              <div className="mt-4">
                <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                  Preferred Employment Type
                </Label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {[
                    { value: "full-time", label: "Full-Time" },
                    { value: "part-time", label: "Part-Time" },
                    { value: "contract", label: "Contract" },
                    { value: "freelance", label: "Freelance" },
                    { value: "temporary", label: "Temporary" },
                    { value: "internship", label: "Internship" }
                  ].map(type => (
                    <label
                      key={type.value}
                      className={`flex items-center gap-2 p-3 border cursor-pointer transition-colors ${
                        (profile.employment_types || []).includes(type.value)
                          ? "border-crimson bg-crimson/10 text-white"
                          : "border-[#333] text-gray-400 hover:border-[#444]"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={(profile.employment_types || []).includes(type.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setProfile(prev => ({
                              ...prev,
                              employment_types: [...(prev.employment_types || []), type.value]
                            }));
                          } else {
                            setProfile(prev => ({
                              ...prev,
                              employment_types: (prev.employment_types || []).filter(t => t !== type.value)
                            }));
                          }
                        }}
                        className="sr-only"
                      />
                      <span className="text-sm">{type.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          </motion.div>

          {/* Skills */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="font-heading text-xl font-bold mb-6">SKILLS</h3>
            
            <div className="flex gap-2 mb-4">
              <Input
                value={newSkill}
                onChange={(e) => setNewSkill(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addSkill()}
                placeholder="Add a skill (e.g., Python, Project Management)"
                className="bg-obsidian border-[#333] text-white placeholder:text-gray-600"
                data-testid="skill-input"
              />
              <Button
                onClick={addSkill}
                className="bg-crimson hover:bg-crimson-hover text-white"
                data-testid="add-skill-btn"
              >
                <Plus className="w-4 h-4" />
              </Button>
            </div>

            <div className="flex flex-wrap gap-2">
              {profile.skills.map((skill, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-crimson/10 border border-crimson/30 text-crimson"
                >
                  {skill}
                  <button onClick={() => removeSkill(skill)} className="hover:text-white">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {profile.skills.length === 0 && (
                <span className="text-gray-500 text-sm">No skills added yet</span>
              )}
            </div>
          </motion.div>

          {/* Work History */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.25 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold">WORK HISTORY</h3>
              <Button
                onClick={() => {
                  setShowAddExperience(!showAddExperience);
                  setEditingExpIndex(null);
                  setNewExperience({
                    title: "",
                    company: "",
                    location: "",
                    start_date: "",
                    end_date: "",
                    is_current: false,
                    description: ""
                  });
                }}
                variant="outline"
                className="border-crimson text-crimson hover:bg-crimson hover:text-white"
                data-testid="add-experience-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Experience
              </Button>
            </div>

            {/* Add/Edit Experience Form */}
            {showAddExperience && (
              <motion.div
                className="bg-obsidian border border-[#333] p-4 mb-6"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <h4 className="font-bold mb-4">
                  {editingExpIndex !== null ? "Edit Experience" : "Add New Experience"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Job Title *
                    </Label>
                    <Input
                      value={newExperience.title}
                      onChange={(e) => setNewExperience(prev => ({ ...prev, title: e.target.value }))}
                      placeholder="e.g., Senior Developer"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="exp-title-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Company *
                    </Label>
                    <Input
                      value={newExperience.company}
                      onChange={(e) => setNewExperience(prev => ({ ...prev, company: e.target.value }))}
                      placeholder="e.g., Tech Corp Inc."
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="exp-company-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Location
                    </Label>
                    <Input
                      value={newExperience.location}
                      onChange={(e) => setNewExperience(prev => ({ ...prev, location: e.target.value }))}
                      placeholder="e.g., New York, NY"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="exp-location-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Start Date
                    </Label>
                    <Input
                      value={newExperience.start_date}
                      onChange={(e) => setNewExperience(prev => ({ ...prev, start_date: e.target.value }))}
                      placeholder="e.g., Jan 2020"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="exp-start-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      End Date
                    </Label>
                    <Input
                      value={newExperience.end_date}
                      onChange={(e) => setNewExperience(prev => ({ ...prev, end_date: e.target.value }))}
                      placeholder="e.g., Dec 2023"
                      className="bg-obsidian-light border-[#333] text-white"
                      disabled={newExperience.is_current}
                      data-testid="exp-end-input"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="is_current"
                      checked={newExperience.is_current}
                      onChange={(e) => setNewExperience(prev => ({ ...prev, is_current: e.target.checked }))}
                      className="w-4 h-4 accent-crimson"
                    />
                    <Label htmlFor="is_current" className="text-gray-300 cursor-pointer">
                      I currently work here
                    </Label>
                  </div>
                </div>
                <div className="mb-4">
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Job Description / Responsibilities
                  </Label>
                  <Textarea
                    value={newExperience.description}
                    onChange={(e) => setNewExperience(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe your key responsibilities, achievements, and skills used..."
                    className="bg-obsidian-light border-[#333] text-white min-h-[100px]"
                    data-testid="exp-description-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={addExperience}
                    className="bg-crimson hover:bg-crimson-hover text-white"
                    data-testid="save-experience-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> 
                    {editingExpIndex !== null ? "Update" : "Add"} Experience
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAddExperience(false);
                      setEditingExpIndex(null);
                    }}
                    variant="outline"
                    className="border-[#333] text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Experience List */}
            {profile.experience?.length > 0 ? (
              <div className="space-y-4">
                {profile.experience.map((exp, index) => (
                  <div 
                    key={index}
                    className="relative pl-6 border-l-2 border-crimson/30 py-2 group"
                  >
                    <div className="absolute -left-[9px] top-3 w-4 h-4 bg-crimson rounded-full" />
                    
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-lg">{exp.title}</h4>
                        <p className="text-crimson flex items-center gap-2">
                          <Building2 className="w-4 h-4" />
                          {exp.company}
                        </p>
                        <p className="text-gray-500 text-sm flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {exp.start_date || exp.duration?.split(" - ")[0] || "N/A"} - {exp.end_date || exp.duration?.split(" - ")[1] || "Present"}
                          </span>
                          {exp.location && (
                            <span>{exp.location}</span>
                          )}
                        </p>
                        {exp.description && (
                          <div className="text-gray-400 text-sm mt-2">
                            {exp.description.split('\n').map((line, i) => (
                              line.trim().startsWith('•') ? (
                                <div key={i} className="pl-4 relative">
                                  <span className="absolute left-0 text-crimson">•</span>
                                  <span>{line.trim().substring(1).trim()}</span>
                                </div>
                              ) : line.trim() ? (
                                <p key={i}>{line}</p>
                              ) : null
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => editExperience(index)}
                          className="p-2 text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                          title="Edit"
                        >
                          <Briefcase className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeExperience(index)}
                          className="p-2 text-gray-500 hover:text-crimson hover:bg-crimson/10 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-[#333]">
                <Briefcase className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No work experience added yet</p>
                <p className="text-gray-600 text-sm">Add your work history manually or import from LinkedIn/Resume</p>
              </div>
            )}
          </motion.div>

          {/* Education */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.26 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                <GraduationCap className="w-5 h-5 text-crimson" />
                EDUCATION
              </h3>
              <Button
                onClick={() => {
                  setShowAddEducation(!showAddEducation);
                  setEditingEduIndex(null);
                  setNewEducation({
                    degree: "",
                    field_of_study: "",
                    school: "",
                    start_year: "",
                    end_year: "",
                    is_current: false,
                    description: ""
                  });
                }}
                variant="outline"
                className="border-crimson text-crimson hover:bg-crimson hover:text-white"
                data-testid="add-education-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Education
              </Button>
            </div>

            {/* Add/Edit Education Form */}
            {showAddEducation && (
              <motion.div
                className="bg-obsidian border border-[#333] p-4 mb-6"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <h4 className="font-bold mb-4">
                  {editingEduIndex !== null ? "Edit Education" : "Add New Education"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Degree / Qualification *
                    </Label>
                    <Input
                      value={newEducation.degree}
                      onChange={(e) => setNewEducation(prev => ({ ...prev, degree: e.target.value }))}
                      placeholder="e.g., Bachelor's, Master's, PhD, Diploma"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="edu-degree-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Field of Study
                    </Label>
                    <Input
                      value={newEducation.field_of_study}
                      onChange={(e) => setNewEducation(prev => ({ ...prev, field_of_study: e.target.value }))}
                      placeholder="e.g., Computer Science, Business Administration"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="edu-field-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      School / Institution *
                    </Label>
                    <Input
                      value={newEducation.school}
                      onChange={(e) => setNewEducation(prev => ({ ...prev, school: e.target.value }))}
                      placeholder="e.g., Stanford University"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="edu-school-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        Start Year
                      </Label>
                      <Input
                        value={newEducation.start_year}
                        onChange={(e) => setNewEducation(prev => ({ ...prev, start_year: e.target.value }))}
                        placeholder="e.g., 2018"
                        className="bg-obsidian-light border-[#333] text-white"
                        data-testid="edu-start-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        End Year
                      </Label>
                      <Input
                        value={newEducation.end_year}
                        onChange={(e) => setNewEducation(prev => ({ ...prev, end_year: e.target.value }))}
                        placeholder="e.g., 2022"
                        className="bg-obsidian-light border-[#333] text-white"
                        disabled={newEducation.is_current}
                        data-testid="edu-end-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="edu_is_current"
                    checked={newEducation.is_current}
                    onChange={(e) => setNewEducation(prev => ({ ...prev, is_current: e.target.checked }))}
                    className="w-4 h-4 accent-crimson"
                  />
                  <Label htmlFor="edu_is_current" className="text-gray-300 cursor-pointer">
                    Currently pursuing this degree
                  </Label>
                </div>
                <div className="mb-4">
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Description (optional)
                  </Label>
                  <Textarea
                    value={newEducation.description}
                    onChange={(e) => setNewEducation(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Notable achievements, activities, or coursework..."
                    className="bg-obsidian-light border-[#333] text-white min-h-[80px]"
                    data-testid="edu-description-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={addEducation}
                    className="bg-crimson hover:bg-crimson-hover text-white"
                    data-testid="save-education-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> 
                    {editingEduIndex !== null ? "Update" : "Add"} Education
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAddEducation(false);
                      setEditingEduIndex(null);
                    }}
                    variant="outline"
                    className="border-[#333] text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Education List */}
            {profile.education?.length > 0 ? (
              <div className="space-y-4">
                {profile.education.map((edu, index) => (
                  <div 
                    key={index}
                    className="relative pl-6 border-l-2 border-blue-500/30 py-2 group"
                  >
                    <div className="absolute -left-[9px] top-3 w-4 h-4 bg-blue-500 rounded-full" />
                    
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-lg">{edu.degree}{edu.field_of_study && ` in ${edu.field_of_study}`}</h4>
                        <p className="text-blue-400 flex items-center gap-2">
                          <GraduationCap className="w-4 h-4" />
                          {edu.school}
                        </p>
                        <p className="text-gray-500 text-sm flex items-center gap-1 mt-1">
                          <Calendar className="w-3 h-3" />
                          {edu.start_year || edu.year || "N/A"}{edu.end_year ? ` - ${edu.end_year}` : ""}
                        </p>
                        {edu.description && (
                          <p className="text-gray-400 text-sm mt-2">{edu.description}</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => editEducation(index)}
                          className="p-2 text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                          title="Edit"
                        >
                          <GraduationCap className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeEducation(index)}
                          className="p-2 text-gray-500 hover:text-crimson hover:bg-crimson/10 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-[#333]">
                <GraduationCap className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No education added yet</p>
                <p className="text-gray-600 text-sm">Add your degrees, diplomas, or certifications</p>
              </div>
            )}
          </motion.div>

          {/* Certifications */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.27 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                <Award className="w-5 h-5 text-yellow-500" />
                CERTIFICATIONS & LICENSES
              </h3>
              <Button
                onClick={() => {
                  setShowAddCertification(!showAddCertification);
                  setEditingCertIndex(null);
                  setNewCertification({
                    name: "",
                    issuing_organization: "",
                    issue_date: "",
                    expiry_date: "",
                    credential_id: "",
                    credential_url: ""
                  });
                }}
                variant="outline"
                className="border-yellow-500 text-yellow-500 hover:bg-yellow-500 hover:text-black"
                data-testid="add-certification-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Certification
              </Button>
            </div>

            {/* Add/Edit Certification Form */}
            {showAddCertification && (
              <motion.div
                className="bg-obsidian border border-[#333] p-4 mb-6"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <h4 className="font-bold mb-4">
                  {editingCertIndex !== null ? "Edit Certification" : "Add New Certification"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Certification Name *
                    </Label>
                    <Input
                      value={newCertification.name}
                      onChange={(e) => setNewCertification(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="e.g., AWS Solutions Architect, PMP"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="cert-name-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Issuing Organization *
                    </Label>
                    <Input
                      value={newCertification.issuing_organization}
                      onChange={(e) => setNewCertification(prev => ({ ...prev, issuing_organization: e.target.value }))}
                      placeholder="e.g., Amazon Web Services, PMI"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="cert-org-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Issue Date
                    </Label>
                    <Input
                      value={newCertification.issue_date}
                      onChange={(e) => setNewCertification(prev => ({ ...prev, issue_date: e.target.value }))}
                      placeholder="e.g., Jan 2023"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="cert-issue-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Expiry Date (optional)
                    </Label>
                    <Input
                      value={newCertification.expiry_date}
                      onChange={(e) => setNewCertification(prev => ({ ...prev, expiry_date: e.target.value }))}
                      placeholder="e.g., Jan 2026 or 'No Expiry'"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="cert-expiry-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Credential ID (optional)
                    </Label>
                    <Input
                      value={newCertification.credential_id}
                      onChange={(e) => setNewCertification(prev => ({ ...prev, credential_id: e.target.value }))}
                      placeholder="e.g., ABC123XYZ"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="cert-id-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Credential URL (optional)
                    </Label>
                    <Input
                      value={newCertification.credential_url}
                      onChange={(e) => setNewCertification(prev => ({ ...prev, credential_url: e.target.value }))}
                      placeholder="https://..."
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="cert-url-input"
                    />
                  </div>
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={addCertification}
                    className="bg-yellow-500 hover:bg-yellow-600 text-black"
                    data-testid="save-certification-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> 
                    {editingCertIndex !== null ? "Update" : "Add"} Certification
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAddCertification(false);
                      setEditingCertIndex(null);
                    }}
                    variant="outline"
                    className="border-[#333] text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Certifications List */}
            {profile.certifications?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {profile.certifications.map((cert, index) => (
                  <div 
                    key={index}
                    className="bg-obsidian border border-[#333] p-4 group relative"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-10 h-10 bg-yellow-500/20 flex items-center justify-center flex-shrink-0">
                        <Award className="w-5 h-5 text-yellow-500" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="font-bold text-white truncate">{cert.name}</h4>
                        <p className="text-gray-400 text-sm">{cert.issuing_organization}</p>
                        <p className="text-gray-500 text-xs mt-1">
                          {cert.issue_date && `Issued: ${cert.issue_date}`}
                          {cert.expiry_date && ` • Expires: ${cert.expiry_date}`}
                        </p>
                        {cert.credential_url && (
                          <a 
                            href={cert.credential_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-crimson text-xs hover:underline flex items-center gap-1 mt-1"
                          >
                            <ExternalLink className="w-3 h-3" /> View Credential
                          </a>
                        )}
                      </div>
                    </div>
                    
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => editCertification(index)}
                        className="p-1.5 text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                        title="Edit"
                      >
                        <Award className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => removeCertification(index)}
                        className="p-1.5 text-gray-500 hover:text-crimson hover:bg-crimson/10 transition-colors"
                        title="Remove"
                      >
                        <Trash2 className="w-3 h-3" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-[#333]">
                <Award className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No certifications added yet</p>
                <p className="text-gray-600 text-sm">Add your professional certifications and licenses</p>
              </div>
            )}
          </motion.div>

          {/* Volunteer Work */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.28 }}
          >
            <div className="flex items-center justify-between mb-6">
              <h3 className="font-heading text-xl font-bold flex items-center gap-2">
                <Heart className="w-5 h-5 text-pink-500" />
                VOLUNTEER WORK
              </h3>
              <Button
                onClick={() => {
                  setShowAddVolunteer(!showAddVolunteer);
                  setEditingVolunteerIndex(null);
                  setNewVolunteer({
                    role: "",
                    organization: "",
                    cause: "",
                    start_date: "",
                    end_date: "",
                    is_current: false,
                    description: ""
                  });
                }}
                variant="outline"
                className="border-pink-500 text-pink-500 hover:bg-pink-500 hover:text-white"
                data-testid="add-volunteer-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> Add Volunteer Work
              </Button>
            </div>

            {/* Add/Edit Volunteer Form */}
            {showAddVolunteer && (
              <motion.div
                className="bg-obsidian border border-[#333] p-4 mb-6"
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
              >
                <h4 className="font-bold mb-4">
                  {editingVolunteerIndex !== null ? "Edit Volunteer Work" : "Add New Volunteer Work"}
                </h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Role *
                    </Label>
                    <Input
                      value={newVolunteer.role}
                      onChange={(e) => setNewVolunteer(prev => ({ ...prev, role: e.target.value }))}
                      placeholder="e.g., Volunteer Coordinator, Tutor"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="vol-role-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Organization *
                    </Label>
                    <Input
                      value={newVolunteer.organization}
                      onChange={(e) => setNewVolunteer(prev => ({ ...prev, organization: e.target.value }))}
                      placeholder="e.g., Red Cross, Local Food Bank"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="vol-org-input"
                    />
                  </div>
                  <div>
                    <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                      Cause (optional)
                    </Label>
                    <Input
                      value={newVolunteer.cause}
                      onChange={(e) => setNewVolunteer(prev => ({ ...prev, cause: e.target.value }))}
                      placeholder="e.g., Education, Environment, Health"
                      className="bg-obsidian-light border-[#333] text-white"
                      data-testid="vol-cause-input"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        Start Date
                      </Label>
                      <Input
                        value={newVolunteer.start_date}
                        onChange={(e) => setNewVolunteer(prev => ({ ...prev, start_date: e.target.value }))}
                        placeholder="e.g., Jan 2020"
                        className="bg-obsidian-light border-[#333] text-white"
                        data-testid="vol-start-input"
                      />
                    </div>
                    <div>
                      <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                        End Date
                      </Label>
                      <Input
                        value={newVolunteer.end_date}
                        onChange={(e) => setNewVolunteer(prev => ({ ...prev, end_date: e.target.value }))}
                        placeholder="e.g., Dec 2023"
                        className="bg-obsidian-light border-[#333] text-white"
                        disabled={newVolunteer.is_current}
                        data-testid="vol-end-input"
                      />
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="vol_is_current"
                    checked={newVolunteer.is_current}
                    onChange={(e) => setNewVolunteer(prev => ({ ...prev, is_current: e.target.checked }))}
                    className="w-4 h-4 accent-pink-500"
                  />
                  <Label htmlFor="vol_is_current" className="text-gray-300 cursor-pointer">
                    I currently volunteer here
                  </Label>
                </div>
                <div className="mb-4">
                  <Label className="text-gray-400 uppercase tracking-wider text-xs mb-2 block">
                    Description (optional)
                  </Label>
                  <Textarea
                    value={newVolunteer.description}
                    onChange={(e) => setNewVolunteer(prev => ({ ...prev, description: e.target.value }))}
                    placeholder="Describe your volunteer activities and impact..."
                    className="bg-obsidian-light border-[#333] text-white min-h-[80px]"
                    data-testid="vol-description-input"
                  />
                </div>
                <div className="flex gap-3">
                  <Button
                    onClick={addVolunteer}
                    className="bg-pink-500 hover:bg-pink-600 text-white"
                    data-testid="save-volunteer-btn"
                  >
                    <Check className="w-4 h-4 mr-2" /> 
                    {editingVolunteerIndex !== null ? "Update" : "Add"} Volunteer Work
                  </Button>
                  <Button
                    onClick={() => {
                      setShowAddVolunteer(false);
                      setEditingVolunteerIndex(null);
                    }}
                    variant="outline"
                    className="border-[#333] text-white"
                  >
                    Cancel
                  </Button>
                </div>
              </motion.div>
            )}

            {/* Volunteer Work List */}
            {profile.volunteer_work?.length > 0 ? (
              <div className="space-y-4">
                {profile.volunteer_work.map((vol, index) => (
                  <div 
                    key={index}
                    className="relative pl-6 border-l-2 border-pink-500/30 py-2 group"
                  >
                    <div className="absolute -left-[9px] top-3 w-4 h-4 bg-pink-500 rounded-full" />
                    
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="font-bold text-lg">{vol.role}</h4>
                        <p className="text-pink-400 flex items-center gap-2">
                          <Heart className="w-4 h-4" />
                          {vol.organization}
                        </p>
                        <p className="text-gray-500 text-sm flex items-center gap-4 mt-1">
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {vol.start_date || "N/A"} - {vol.end_date || "Present"}
                          </span>
                          {vol.cause && (
                            <span className="px-2 py-0.5 bg-pink-500/10 text-pink-400 text-xs">
                              {vol.cause}
                            </span>
                          )}
                        </p>
                        {vol.description && (
                          <p className="text-gray-400 text-sm mt-2">{vol.description}</p>
                        )}
                      </div>
                      
                      <div className="flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => editVolunteer(index)}
                          className="p-2 text-gray-500 hover:text-white hover:bg-white/10 transition-colors"
                          title="Edit"
                        >
                          <Heart className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => removeVolunteer(index)}
                          className="p-2 text-gray-500 hover:text-crimson hover:bg-crimson/10 transition-colors"
                          title="Remove"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 border border-dashed border-[#333]">
                <Heart className="w-12 h-12 text-gray-600 mx-auto mb-3" />
                <p className="text-gray-500 mb-2">No volunteer work added yet</p>
                <p className="text-gray-600 text-sm">Showcase your community involvement and giving back</p>
              </div>
            )}
          </motion.div>

          {/* Industries */}
          <motion.div 
            className="bg-obsidian-light border border-[#222] p-6 mb-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h3 className="font-heading text-xl font-bold mb-6">INDUSTRIES OF INTEREST</h3>
            
            <div className="flex gap-2 mb-4">
              <select
                value={newIndustry}
                onChange={(e) => {
                  if (e.target.value && !profile.industries.includes(e.target.value)) {
                    setProfile(prev => ({ ...prev, industries: [...prev.industries, e.target.value] }));
                  }
                  setNewIndustry("");
                }}
                className="flex-1 h-10 px-3 bg-obsidian border border-[#333] text-white focus:border-crimson focus:ring-1 focus:ring-crimson"
                data-testid="industry-select"
              >
                <option value="">Select an industry to add</option>
                {industries.filter(ind => !profile.industries.includes(ind)).map(industry => (
                  <option key={industry} value={industry}>{industry}</option>
                ))}
              </select>
            </div>

            <div className="flex flex-wrap gap-2">
              {profile.industries.map((industry, index) => (
                <span
                  key={index}
                  className="inline-flex items-center gap-2 px-3 py-1 bg-white/5 border border-[#333] text-white"
                >
                  {industry}
                  <button onClick={() => removeIndustry(industry)} className="hover:text-crimson">
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              {profile.industries.length === 0 && (
                <span className="text-gray-500 text-sm">No industries added yet</span>
              )}
            </div>
          </motion.div>

          {/* Save Button */}
          <div className="flex justify-end">
            <Button
              onClick={handleSaveProfile}
              disabled={loading}
              className="bg-crimson hover:bg-crimson-hover text-white font-bold uppercase tracking-wider px-8 py-6"
              data-testid="save-profile-bottom-btn"
            >
              {loading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" /> Save & Continue
                </>
              )}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ProfileBuilder;
